import { LocalizedText } from "../../apis/API";
import {
  ResourceLocationItem,
  ResourceLocationView,
  toLocationViews,
} from "./resourceLocations";

/** The category fields selected by LOAD_AGENCY_RESOURCES. */
export type AgencyResourceCategory = {
  id?: string | null;
  deletedAt?: number | null;
  localization?: Array<LocalizedText | null> | null;
};

/** The resource fields selected by LOAD_AGENCY_RESOURCES. */
export type AgencyResourceItem = {
  id?: string | null;
  title?: string | null;
  description?: string | null;
  keywords?: Array<string | null> | null;
  categoryId?: string | null;
  category?: AgencyResourceCategory | null;
  mainLocation?: ResourceLocationItem | null;
  locations?: Array<ResourceLocationItem | null> | null;
};

/** A resource ready to render: nulls resolved, category label localized. */
export type ResourceViewModel = {
  id: string;
  title: string;
  description: string;
  keywords: string[];
  categoryId: string | null;
  categoryLabel: string | null;
  /** Locale the label text is in, for the `lang` attribute (SC 3.1.2). */
  categoryLocale: string | null;
  mainLocation?: ResourceLocationView;
  /** Every other location, in the order the agency entered them. */
  otherLocations: ResourceLocationView[];
};

export type CategoryOption = {
  id: string;
  label: string;
  locale: string | null;
};

const baseLanguage = (tag: string): string => tag.split("-")[0].toLowerCase();

/**
 * The category label in the participant's language. Matches on the base
 * language only, so a browser set to es-MX still finds a label stored as
 * es-ES. A deleted category has no label: the backend joins categories
 * without checking `deletedAt`, and iOS never shows deleted categories.
 */
export const resolveCategoryLabel = (
  category: AgencyResourceCategory | null | undefined,
  language: string,
): { text: string; locale: string | null } | undefined => {
  if (!category || category.deletedAt) return undefined;
  const entries = (category.localization ?? []).filter(
    (entry): entry is LocalizedText & { text: string } => Boolean(entry?.text),
  );
  const wanted = baseLanguage(language);
  const match =
    entries.find((entry) => baseLanguage(entry.locale ?? "") === wanted) ??
    entries[0];
  if (!match) return undefined;
  return { text: match.text, locale: match.locale || null };
};

/**
 * Turns the query result into render-ready resources, sorted by category
 * label then title. The backend orders by an opaque category id, which reads
 * as random to a participant. Resources without a category sort last.
 */
export const toResourceViewModels = (
  resources: Array<AgencyResourceItem | null> | null | undefined,
  language: string,
): ResourceViewModel[] => {
  const collator = new Intl.Collator(language, { sensitivity: "base" });
  return (resources ?? [])
    .filter((resource): resource is AgencyResourceItem => Boolean(resource?.id))
    .map((resource) => {
      const label = resolveCategoryLabel(resource.category, language);
      const { main, others } = toLocationViews(
        resource.mainLocation,
        resource.locations,
      );
      return {
        id: resource.id as string,
        title: resource.title ?? "",
        description: resource.description ?? "",
        keywords: (resource.keywords ?? []).filter(
          (keyword): keyword is string => Boolean(keyword),
        ),
        // The model defaults categoryId to "" rather than null.
        categoryId: resource.categoryId || null,
        categoryLabel: label?.text ?? null,
        categoryLocale: label?.locale ?? null,
        mainLocation: main,
        otherLocations: others,
      };
    })
    .sort((a, b) => {
      if (a.categoryLabel !== b.categoryLabel) {
        if (a.categoryLabel === null) return 1;
        if (b.categoryLabel === null) return -1;
        const byCategory = collator.compare(a.categoryLabel, b.categoryLabel);
        if (byCategory !== 0) return byCategory;
      }
      return collator.compare(a.title, b.title);
    });
};

/**
 * Categories that have at least one resource and a label, in display order.
 * Resources with no usable category are still listed under "All".
 */
export const categoryOptions = (
  resources: ResourceViewModel[],
): CategoryOption[] => {
  const seen = new Set<string>();
  const options: CategoryOption[] = [];
  resources.forEach((resource) => {
    if (
      resource.categoryId &&
      resource.categoryLabel &&
      !seen.has(resource.categoryId)
    ) {
      seen.add(resource.categoryId);
      options.push({
        id: resource.categoryId,
        label: resource.categoryLabel,
        locale: resource.categoryLocale,
      });
    }
  });
  return options;
};

/** `null` means all categories. */
export const filterByCategory = (
  resources: ResourceViewModel[],
  categoryId: string | null,
): ResourceViewModel[] =>
  categoryId === null
    ? resources
    : resources.filter((resource) => resource.categoryId === categoryId);

/** Matches iOS: the picker only appears when two or more categories have resources. */
export const shouldShowCategoryPicker = (options: CategoryOption[]): boolean =>
  options.length >= 2;

/**
 * Text folded for search the way iOS compares it (`.caseInsensitive`,
 * `.diacriticInsensitive`): accents dropped after canonical decomposition,
 * then lower-cased without a locale, so a Turkish browser still matches "i".
 * Letters with no combining mark (ł, ø, ß) are not folded, as on iOS.
 */
export const normalizeForSearch = (text: string): string =>
  text
    .normalize("NFD")
    .replace(/\p{M}+/gu, "")
    .toLowerCase();

/**
 * Whether the resource matches the query: a substring of its title, its
 * keywords joined with spaces, or its description (the fields iOS searches).
 * An empty or blank query matches everything.
 */
export const matchesSearch = (
  resource: ResourceViewModel,
  query: string,
): boolean => {
  const needle = normalizeForSearch(query.trim());
  if (!needle) return true;
  return [resource.title, resource.keywords.join(" "), resource.description]
    .map(normalizeForSearch)
    .some((field) => field.includes(needle));
};

/** The category filter, then the search. Runs on the loaded list only. */
export const filterResources = (
  resources: ResourceViewModel[],
  categoryId: string | null,
  query: string,
): ResourceViewModel[] =>
  filterByCategory(resources, categoryId).filter((resource) =>
    matchesSearch(resource, query),
  );
